import * as Sentry from '@sentry/react';
import type Matter from 'matter-js'; // IEventCollision 型参照で使用
import type {
  Application,
  Container,
  FederatedPointerEvent,
  Graphics,
} from 'pixi.js';
import type { FallingBlock } from '@/canvas/components/FallingBlock';
import type { BlockManager } from '@/canvas/systems/BlockManager';
import type { CollisionHandler } from '@/canvas/systems/CollisionHandler';
import {
  createBoundaryCollisionCallback,
  drawBoundaryBoxes,
} from '@/canvas/systems/gameEngineBoundary';
import {
  attachCanvasToDom,
  createDebugGraphics,
  createGameSystems,
  createPixiApp,
  createRootContainer,
} from '@/canvas/systems/gameEngineInit';
import {
  createGlobalPointerMoveHandler,
  createPointerEventHandlers,
  registerContainerPointerEvents,
  registerGlobalPointerMove,
} from '@/canvas/systems/gameEngineInput';
import {
  DEBUG,
  drawBackground,
  drawBox,
  drawDebugCollisions,
} from '@/canvas/systems/gameEngineRender';
import { PhysicsEngine } from '@/canvas/systems/PhysicsEngine';
import { useGameStore } from '@/store/gameStore';

/**
 * GameEngineのコールバック
 * React層との連携に必須。GameEngineはデータ永続化を行わないため、
 * すべての状態変更はこれらのコールバック経由でReact側に通知される必要があります。
 */
export interface GameEngineCallbacks {
  /** ゲーム終了時のコールバック（必須）。境界衝突・ESC押下などいずれの場合も呼ばれる。 */
  onGameEnd: (finalScore: number) => void;
}

/**
 * GameEngineの設定オプション
 */
export interface GameEngineOptions {
  /** コールバック関数 */
  callbacks: GameEngineCallbacks;
  /** SE音量 (0-1の範囲、デフォルト: 0.5) */
  seVolume?: number;
  /** ブロック結合を有効化するか（検証用、デフォルト: true） */
  enableBlockMerge?: boolean;
}

/**
 * ゲームエンジンのメインクラス
 * PixiJSアプリケーションとゲームロジックの統合を担当
 */
export class GameEngine {
  private readonly screenWidth: number = 400;
  private readonly screenHeight: number = 700;

  private readonly boxWallThickness: number = 40;
  private readonly spawnOffsetAboveBox: number = 100;
  private readonly boxWidthRatio: number = 0.9;
  private readonly boxHeightRatio: number = 0.82;
  private readonly boxTopRatio: number = 0.2;

  private readonly nextBlockDelayMs: number = 1000;

  private app!: Application;
  private container!: Container;
  private physicsEngine!: PhysicsEngine;
  private blockManager!: BlockManager;
  private collisionHandler!: CollisionHandler;

  private boundaryBoxes: ReturnType<PhysicsEngine['getBoundaryBoxes']> = null;
  private boundaryCollisionCallback:
    | ((event: Matter.IEventCollision<Matter.Engine>) => void)
    | null = null;

  private boxX: number;
  private boxY: number;
  private boxWidth: number;
  private boxHeight: number;

  private readonly boundaryThickness: number = 50;

  private isPointerDown: boolean = false;

  private isGameEnded: boolean = false;
  private pendingGameEnd: boolean = false;
  private isWaitingForNextBlock: boolean = false;

  private callbacks?: GameEngineCallbacks;

  private enableBlockMerge: boolean;

  private debugGraphics: Graphics | null = null;

  private boundGlobalPointerMove:
    | ((event: FederatedPointerEvent) => void)
    | null = null;

  /** 管理中の FallingBlock を直接追跡するセット */
  private activeBlocks: Set<FallingBlock> = new Set();

  private keydownHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.handleGameEnd();
    }
  };

  constructor(
    private containerElement: HTMLElement,
    options: GameEngineOptions,
  ) {
    this.callbacks = options.callbacks;
    this.enableBlockMerge = options.enableBlockMerge ?? true;

    this.boxWidth = this.screenWidth * this.boxWidthRatio;
    this.boxHeight = this.screenHeight * this.boxHeightRatio;
    this.boxX = (this.screenWidth - this.boxWidth) / 2;
    this.boxY = this.screenHeight * this.boxTopRatio;
  }

  async init(): Promise<void> {
    this.app = await createPixiApp(this.screenWidth, this.screenHeight);

    this.container = createRootContainer(this.app);

    attachCanvasToDom(
      this.containerElement,
      this.app.canvas as HTMLCanvasElement,
    );

    this.physicsEngine = new PhysicsEngine();

    const { blockManager, collisionHandler } = createGameSystems({
      boxX: this.boxX,
      boxY: this.boxY,
      boxWidth: this.boxWidth,
      spawnOffsetAboveBox: this.spawnOffsetAboveBox,
      container: this.container,
      physicsEngine: this.physicsEngine,
      enableBlockMerge: this.enableBlockMerge,
    });
    this.blockManager = blockManager;
    this.collisionHandler = collisionHandler;
    this.collisionHandler.setActiveBlocks(this.activeBlocks);

    this.isGameEnded = false;
    this.isWaitingForNextBlock = false;

    this.setupKeyboardEvents();

    drawBackground(this.container, this.screenWidth, this.screenHeight);
    drawBox(this.container, {
      boxX: this.boxX,
      boxY: this.boxY,
      boxWidth: this.boxWidth,
      boxHeight: this.boxHeight,
      boxWallThickness: this.boxWallThickness,
    });

    if (DEBUG.showBoundaryBoxes) {
      drawBoundaryBoxes(
        this.container,
        this.screenWidth,
        this.screenHeight,
        this.boundaryThickness,
      );
    }

    this.physicsEngine.createBox(
      this.boxX,
      this.boxY,
      this.boxWidth,
      this.boxHeight,
      this.boxWallThickness,
    );

    this.physicsEngine.createBoundaryBoxes(
      this.screenWidth,
      this.screenHeight,
      this.boundaryThickness,
    );

    this.boundaryBoxes = this.physicsEngine.getBoundaryBoxes();
    if (this.boundaryBoxes) {
      this.boundaryCollisionCallback = createBoundaryCollisionCallback({
        isGameEnded: () => this.isGameEnded,
        isPendingGameEnd: () => this.pendingGameEnd,
        boundaryBoxes: this.boundaryBoxes,
        setPendingGameEnd: (value: boolean) => {
          this.pendingGameEnd = value;
        },
      });
      this.physicsEngine.onCollision(this.boundaryCollisionCallback);
    }

    try {
      await this.blockManager.loadBlockConfigs();

      this.blockManager.createRandomBlock(
        this.physicsEngine,
        this.container,
        this.activeBlocks,
      );
      this.blockManager.selectNextDropId();
    } catch (error) {
      console.error('Failed to initialize blocks:', error);
      throw error;
    }

    this.setupMouseEvents();

    if (this.app.ticker) {
      this.app.ticker.add((ticker) => {
        this.update(ticker.deltaMS);
      });
    } else {
      console.error('Ticker is not available');
      const err = new Error('Failed to initialize game ticker');
      Sentry.captureException(err);
      throw err;
    }
  }

  private setupKeyboardEvents(): void {
    window.addEventListener('keydown', this.keydownHandler);
  }

  private setupMouseEvents(): void {
    const pointerHandlers = createPointerEventHandlers({
      isGameEnded: () => this.isGameEnded,
      isPointerDown: () => this.isPointerDown,
      setPointerDown: (value: boolean) => {
        this.isPointerDown = value;
      },
      updateBlockPosition: (x: number) => {
        this.blockManager.updateCurrentBlockPosition(x);
      },
      handleClick: () => {
        this.handleClick();
      },
    });
    registerContainerPointerEvents(this.container, pointerHandlers);

    this.boundGlobalPointerMove = createGlobalPointerMoveHandler({
      isGameEnded: () => this.isGameEnded,
      isPointerDown: () => this.isPointerDown,
      updateBlockPosition: (x: number) => {
        this.blockManager.updateCurrentBlockPosition(x);
      },
    });
    registerGlobalPointerMove(
      this.app.stage,
      this.app.screen,
      this.boundGlobalPointerMove,
    );
  }

  private handleClick(): void {
    if (this.isWaitingForNextBlock || this.isGameEnded) return;
    this.isWaitingForNextBlock = true;

    const nextId = this.blockManager.getNextDropBlockId();
    this.blockManager.dropCurrentBlock();

    setTimeout(() => {
      if (!this.isGameEnded) {
        if (nextId !== null) {
          this.blockManager.createBlockById(
            nextId,
            this.physicsEngine,
            this.container,
            this.activeBlocks,
          );
        } else {
          this.blockManager.createRandomBlock(
            this.physicsEngine,
            this.container,
            this.activeBlocks,
          );
        }
        this.blockManager.selectNextDropId();
      }
      this.isWaitingForNextBlock = false;
    }, this.nextBlockDelayMs);
  }

  private handleGameEnd(): void {
    if (this.isGameEnded) return;
    this.isGameEnded = true;
    const finalScore = useGameStore.getState().score;

    if (!this.callbacks?.onGameEnd) {
      const err = new Error(
        'GameEngine: onGameEnd callback is required but not provided',
      );
      Sentry.captureException(err);
      throw err;
    }
    this.callbacks.onGameEnd(finalScore);
  }

  public endGame(): void {
    this.handleGameEnd();
  }

  /** activeBlocks セットへの参照を返す（CollisionHandler 等から利用） */
  getActiveBlocks(): Set<FallingBlock> {
    return this.activeBlocks;
  }

  private update(elapsedMs: number): void {
    if (this.isGameEnded) return;

    // collisionStart コールバック内でゲーム終了フラグが立っていた場合、
    // Matter.Engine.update 完了後のここで安全に終了処理を実行する
    if (this.pendingGameEnd) {
      this.pendingGameEnd = false;
      this.handleGameEnd();
      return;
    }

    this.physicsEngine.update(elapsedMs);

    // Matter.Engine.update 完了後にマージキューを処理する。
    // collisionStart 内でワールドを書き換えると Resolver がクラッシュするため遅延実行。
    this.collisionHandler.processMergeQueue();

    for (const block of this.activeBlocks) {
      block.update();
    }

    if (DEBUG.showCollisionShapes) {
      if (!this.debugGraphics) {
        this.debugGraphics = createDebugGraphics(this.container);
      }
      drawDebugCollisions(this.debugGraphics, this.activeBlocks);
    }
  }

  destroy(): void {
    window.removeEventListener('keydown', this.keydownHandler);

    if (this.app?.stage) {
      if (this.boundGlobalPointerMove) {
        this.app.stage.off('globalpointermove', this.boundGlobalPointerMove);
      }
    }

    if (this.boundaryCollisionCallback) {
      this.physicsEngine.offCollision(this.boundaryCollisionCallback);
      this.boundaryCollisionCallback = null;
    }
    this.collisionHandler.destroy();
    this.physicsEngine.destroy();

    if (this.debugGraphics) {
      this.debugGraphics.destroy();
      this.debugGraphics = null;
    }

    this.activeBlocks.clear();
    this.container.destroy({ children: true });

    this.app.destroy(true, { children: true });
  }
}
